import { io, Socket } from 'socket.io-client';
import apiClient from './apiClient';

interface DeviceStatusChangedPayload {
  deviceId: number;
  userId: string;
  isActive: boolean;
  isPaired: boolean;
  deviceName: string;
}

interface SocketTokenResponse {
  success: boolean;
  token: string;
}

// Obtener la URL base del WebSocket (sin el namespace)
const WS_BASE_URL = import.meta.env.VITE_WEBSOCKET_URL ||
                    (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
                    window.location.host;

// Eliminar cualquier path existente y agregar el namespace correcto
const SMS_SOCKET_URL = WS_BASE_URL.replace(/\/(ws|sms-status)$/, '') + '/sms-status';

let smsSocket: Socket | null = null;
let connectingPromise: Promise<Socket | null> | null = null;

async function fetchSocketToken(): Promise<string> {
  // apiClient's response interceptor already unwraps `response.data`, so the
  // awaited value here IS the body, not an AxiosResponse (see apiClient.ts).
  const response = (await apiClient.get(
    '/sms/devices/socket-token'
  )) as unknown as SocketTokenResponse;
  return response.token;
}

export const initSmsWebSocket = async (): Promise<Socket | null> => {
  if (smsSocket && smsSocket.connected) {
    console.log('[SMS WebSocket] Already connected.');
    return smsSocket;
  }

  // Single-flight guard: concurrent callers share the same in-flight
  // token-fetch + connect instead of racing multiple sockets/tokens.
  if (connectingPromise) {
    return connectingPromise;
  }

  connectingPromise = (async () => {
    try {
      const token = await fetchSocketToken();

      console.log(`[SMS WebSocket] Connecting to ${SMS_SOCKET_URL}`);
      smsSocket = io(SMS_SOCKET_URL, {
        reconnectionAttempts: 5,
        reconnectionDelay: 3000,
        withCredentials: false, // Cambiar a false para permitir origin: '*'
        path: '/socket.io/', // Path estándar de Socket.IO
        auth: { token },
      });

      smsSocket.on('connect', () => {
        console.log('[SMS WebSocket] Connected:', smsSocket?.id);
      });

      smsSocket.on('disconnect', (reason) => {
        console.log('[SMS WebSocket] Disconnected:', reason);
      });

      smsSocket.on('connect_error', (error) => {
        console.error('[SMS WebSocket] Connection error:', error);
      });

      return smsSocket;
    } catch (error) {
      console.error('[SMS WebSocket] Failed to fetch socket token:', error);
      return null;
    } finally {
      connectingPromise = null;
    }
  })();

  return connectingPromise;
};

export const getSmsSocket = async (): Promise<Socket | null> => {
  if (!smsSocket) {
    return initSmsWebSocket();
  }
  return smsSocket;
};

export const subscribeToDeviceStatusChanged = async (
  callback: (data: DeviceStatusChangedPayload) => void
): Promise<void> => {
  const socket = await getSmsSocket();
  if (socket) {
    socket.on('device:status-changed', callback);
    console.log('[SMS WebSocket] Subscribed to device:status-changed');
  } else {
    console.warn('[SMS WebSocket] Cannot subscribe. Socket not available.');
  }
};

export const unsubscribeFromDeviceStatusChanged = async (
  callback?: (data: DeviceStatusChangedPayload) => void
): Promise<void> => {
  const socket = await getSmsSocket();
  if (socket) {
    if (callback) {
      socket.off('device:status-changed', callback);
    } else {
      socket.off('device:status-changed');
    }
    console.log('[SMS WebSocket] Unsubscribed from device:status-changed');
  }
};

export const disconnectSmsWebSocket = (): void => {
  if (smsSocket) {
    smsSocket.disconnect();
    smsSocket = null;
    console.log('[SMS WebSocket] Disconnected and cleared');
  }
};
